import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import './Section.scss'


/**
 * Primary UI component for user interaction
 */
export const Section = ({ sectionClass, collapsed, heading, headingLarge, sectionId, textAfter, textBefore, tracking, trackEvent, visuallyHidden, ...props }) => {
  const sectionHeadingId = heading ? `section_${sectionId}_label` : null
  const sectionClassName = sectionClass ? sectionClass : ''
  const [debounceTimer, debounceTimerSet]     = useState(null)
  const [hasTrackedEntry, hasTrackedEntrySet] = useState((tracking && tracking.type === 'exit') ? true : false)
  const [hasTrackedExit, hasTrackedExitSet]   = useState(false)
  const sectionRef       = useRef()

  /**
   * Check if the section should track events.
   *
   * @returns {boolean}
   */
  const trackingShouldTrack = () => {
    if (tracking && typeof tracking.type === 'string') {
      return (['entry', 'exit', 'entry_exit']).includes(tracking.type)
    }

    return false
  }

  /**
   * Push the section entry event.
   */
  const pushEntryEvent = () => {
    if (!hasTrackedEntry && trackEvent) {
      trackEvent(tracking.id, `${tracking.name} entered`)
      hasTrackedEntrySet(true)
    }
  }

  /**
   * Push the section exit event.
   */
  const pushExitEvent = () => {
    if (!hasTrackedExit && trackEvent) {
      trackEvent(tracking.id, `${tracking.name} exited`)
      hasTrackedExitSet (true)
    }
  }

  /**
   * Handle the observable event.
   *
   * @param {[]} entries
   *   The observable entries.
   */
  const handleObservable = entries => {
    const [entry] = entries

    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    if (entry.isIntersecting && tracking.type !== 'exit') {
      debounceTimerSet(setTimeout(() => pushEntryEvent(), 2000))
    }

    if (entry.boundingClientRect.height + entry.boundingClientRect.top < 0  && tracking.type !== 'entry') {
      debounceTimerSet(setTimeout(() => pushExitEvent(), 2000))
    }
  }


  if (trackingShouldTrack()) {
    useEffect(() => {
      if (sectionRef.current) {
        const observer = new IntersectionObserver(entries => handleObservable(entries), {
          root      : null,
          rootMargin: '0px',
          threshold : [0]
        })

        observer.observe(sectionRef.current)

        return (() => {
          observer.unobserve(sectionRef.current)
        })
      }
    }, [sectionRef, hasTrackedExit, hasTrackedEntry])
  }

  return (
    <section ref={sectionRef} role={'group'} className={`m-section m-section__row ${sectionClassName && 'm-section__' + sectionClassName}`} aria-labelledby={sectionHeadingId} data-collapsed={collapsed} data-visually-hidden={visuallyHidden}>

      {heading && headingLarge ?
        <div className="m-section__heading" id={sectionHeadingId}>
          <h2 className="h1">{heading}</h2>
        </div> :
        null
      }

      {heading && !headingLarge ?
        <div className="m-section__heading" id={sectionHeadingId}>
          <h2 className="">{heading} </h2>
        </div> :
        null
      }

      <div className="m-section__inner">
        {textBefore ? <div className="m-section__row"><div dangerouslySetInnerHTML={{ __html: textBefore }} /></div> : null}

        {React.Children.map(props.children, child => {
          return (child ? <div className="m-section__row">{child}</div> : null)
        })}

        {textAfter ? <div className="m-section__row"><div dangerouslySetInnerHTML={{ __html: textAfter }} /></div> : null}
      </div>
    </section>
  )
}

Section.propTypes = {
  collapsed     : PropTypes.bool,
  heading       : PropTypes.string,
  headingLarge  : PropTypes.bool,
  sectionId     : PropTypes.string,
  textAfter     : PropTypes.string,
  textBefore    : PropTypes.string,
  visuallyHidden: PropTypes.bool,
  tracking      : PropTypes.shape({
    id  : PropTypes.number,
    name: PropTypes.string,
    type: PropTypes.oneOf(['entry', 'exit', 'entry_exit'])
  }),
  trackEvent: PropTypes.func
}
